import { useContext, createContext, useState, useMemo } from "react"
import { cloneDeep } from "lodash"

import { useQueryClient } from "@tanstack/react-query"

// dayjs
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

// api
import {
  usePostForceWorkflow,
  usePostCancelWorkflow,
  usePostHoldWorkflow,
  usePostUnholdWorkflow,
} from "api/hooks/workflows/useWorkflowsApi"

export const WorkflowContext = createContext({})

dayjs.extend(utc)
dayjs.extend(timezone)

export const WorkflowProvider = ({ children }) => {
  const queryClient = useQueryClient()

  /** LOCAL STATE (could pass as props) */
  const [workflowId, setWorkflowId] = useState(null)
  const [workflowModel, setWorkflowModel] = useState(null)
  const [selectedAction, setSelectedAction] = useState(null)
  const [openForceModal, setOpenForceModal] = useState(null)
  const [openEditStatesModal, setOpenEditStatesPanel] = useState(false)
  const [openCancelWorkflowModal, setOpenCancelWorkflowModal] = useState(false)
  const [parentQueries, setParentQueries] = useState([])

  // parentQueries set by Activity and Request pages
  const invalidateParentQueries = () => {
    if (parentQueries) {
      parentQueries.forEach((query) => {
        queryClient.invalidateQueries({ queryKey: query })
      })
    }
  }

  /** API MUTATE FUNCTIONS (could localize) */
  const forceWorkflow = usePostForceWorkflow({ workflowId, workflowModel })
  const cancelWorkflow = usePostCancelWorkflow({ workflowId, workflowModel })
  const holdWorkflow = usePostHoldWorkflow({ workflowId, workflowModel })
  const unholdWorkflow = usePostUnholdWorkflow({ workflowId, workflowModel })

  /** TODO: SERVER STATE - do not save server state into local state */
  const [workflowTrackerData, setWorkflowTrackerData] = useState([])

  // date formatting functions
  const convertUtcToLocal = (utcDate) => {
    if (!utcDate) return null
    return dayjs(utcDate).tz(dayjs.tz.guess()).format("YYYY-MM-DD")
  }

  // const getLatterTz = (state, date) => {
  //   const now = dayjs().format("YYYY-MM-DD")
  //   const startTz = state?.start_state_date?.split("T")?.[1]
  //   const endTz = state?.end_state_date?.split("T")?.[1]
  //   const splitDate = date?.split("T")?.[0]

  //   const startWithTz = `${now}T${startTz}`
  //   const endWithTz = `${now}T${endTz}`

  //   if (startTz && endTz) {
  //     if (dayjs(startWithTz).isAfter(endWithTz)) {
  //       return `${splitDate}T${startTz}`
  //     } else {
  //       return `${splitDate}T${endTz}`
  //     }
  //   } else if (startTz) {
  //     return `${splitDate}T${startTz}`
  //   } else {
  //     return `${splitDate}T${endTz}`
  //   }
  // }

  const now = dayjs()
  const formatToUtc = (dateStr) => {
    const inputDate = dayjs(dateStr)

    const localWithCurrentTime = inputDate
      .local()
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond())

    return localWithCurrentTime.utc()
  }

  // Date difference and add functions
  const getDifferenceBetweenDays = (a, b) => {
    if (!a || !b) {
      throw new Error('Both parameters "a" and "b" must be defined')
    }
    return Math.abs(dayjs(a).diff(dayjs(b), "day"))
  }

  const addDays = (date, days) => {
    return dayjs(date).add(days, "day")
  }

  /** TODO: make following derived (from server) state into pure functions or just localize */

  const isWorkflowCancelled = useMemo(() => {
    return workflowTrackerData?.workflow_data?.is_cancelled
  }, [workflowTrackerData?.workflow_data?.is_cancelled])

  const isOnHold = useMemo(() => {
    return workflowTrackerData?.workflow_data?.is_onhold
  }, [workflowTrackerData?.workflow_data?.is_onhold])

  const isWorkflowComplete = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const td = workflowTrackerData?.state_data?.tracker_data
      return td[td.length - 1]?.is_current ? true : false
    }
  }, [workflowTrackerData?.state_data])

  const currentState = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const curr = workflowTrackerData?.state_data?.tracker_data.find((state) => state.is_current)
      if (curr) return curr
    }
    return null
  }, [workflowTrackerData?.state_data?.tracker_data])

  const projectedCompletionDate = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const tracker = workflowTrackerData.state_data.tracker_data
      let lastState = tracker[tracker.length - 1]
      return lastState.start_state_date
    }
    return null
  }, [workflowTrackerData?.state_data?.tracker_data])

  const completedStates = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      return workflowTrackerData.state_data.tracker_data.filter((state) => state.status === "Completed")
    }
    return []
  }, [workflowTrackerData?.state_data?.tracker_data])

  const payloadCleanup = (payload) => {
    const keysToDelete = [
      "widthPercent",
      "assigned_to_ids",
      "assigned_to_names",
      "state_title",
      "list_order",
      "is_current",
      "state_description",
      "allow_resend",
      "milestones",
      "isToday",
      "state_id",
    ]

    const cleanedPayload = { ...payload }
    keysToDelete.forEach((key) => {
      delete cleanedPayload[key]
    })
    return cleanedPayload
  }

  const prevStateChangeHandler = (tempData, index) => {
    for (let i = index - 1; i >= 0; i--) {
      const currState = tempData[i]
      const nextState = tempData[i + 1]
      if (currState.end_state_date === null || currState.end_state_date === "Invalid Date") {
        currState.end_state_date = nextState.start_state_date
      }
      const goalDays = getDifferenceBetweenDays(tempData[i].end_state_date, tempData[i].start_state_date)

      const newStartDate = dayjs(nextState.start_state_date).subtract(goalDays, "day").format("YYYY-MM-DD")

      currState.start_state_date = newStartDate
      currState.end_state_date = nextState.start_state_date
      currState.goal_days = goalDays
    }
    return tempData
  }

  const nextStateChangeHandler = (tempData, index) => {
    for (let i = index + 1; i < tempData.length; i++) {
      const prevState = tempData[i - 1]
      const currState = tempData[i]
      if (currState.end_state_date === null || currState.end_state_date === "Invalid Date") {
        currState.end_state_date = tempData[i].start_state_date
      }
      const goalDays = getDifferenceBetweenDays(tempData[i].end_state_date, tempData[i].start_state_date)

      const newEndDate = dayjs(prevState.end_state_date).add(goalDays, "day").format("YYYY-MM-DD")

      currState.start_state_date = prevState.end_state_date
      currState.end_state_date = newEndDate
      currState.goal_days = goalDays
    }

    return tempData
  }

  const formatPutReq = ({
    stateData,
    model,
    workflowTrackerData,
    dueDateType,
    manualDueDateValue,
    selectedAssessment,
    values,
    state,
    updatedState = false,
  }) => {
    let activityDueDate = null
    let assessmentPk = null
    let projectionCompletionDate = null
    let actionData = []

    if (dueDateType === "manual") {
      activityDueDate = dayjs(manualDueDateValue).format("YYYY-MM-DD")
    } else if (selectedAssessment?.assessment_pk && selectedAssessment?.assessment_due_date) {
      assessmentPk = selectedAssessment.assessment_pk
      activityDueDate = dayjs(selectedAssessment.assessment_due_date).format("YYYY-MM-DD")
    }

    const getStartDate = (state, tempData, i) => {
      if (i > 0 && tempData[i - 1].end_state_date) {
        return dayjs(state.end_state_date).format("YYYY-MM-DD")
      } else {
        return state.start_state_date
      }
    }

    const getEndDate = (state, tempData, i) => {
      if (i !== tempData.length - 1) {
        return dayjs(state.start_state_date).add(state.goal_days, "day").format("YYYY-MM-DD")
      } else {
        return state?.end_state_date
      }
    }

    if (updatedState && values && state) {
      let tempData = [...stateData]
      const foundIndex = tempData.findIndex((item) => item.state_id === state.state_id)

      // Update the found state in the tempData array
      tempData = tempData.map((item, i) => {
        if (item.state_id === state.state_id) {
          return {
            ...item,
            start_state_date: dayjs(values.start_date).format("YYYY-MM-DD"),
            end_state_date: dayjs(values.end_date).format("YYYY-MM-DD"),
            goal_days: values.goal_days || 0,
            assign_to_ids: values.assignee || [],
          }
        }

        return {
          ...item,
          start_state_date: getStartDate(item, tempData.i),
          end_state_date: getEndDate(item, tempData, i),
          goal_days: item.goal_days || 0,
        }
      })

      tempData = prevStateChangeHandler(tempData, foundIndex)
      tempData = nextStateChangeHandler(tempData, foundIndex)

      stateData = tempData
    }

    const lastState = stateData[stateData.length - 1]
    if (!isOnHold) {
      if (lastState.end_state_date === "Invalid Date" || lastState.end_state_date === null) {
        projectionCompletionDate = dayjs(lastState.start_state_date)
          .add(lastState.goal_days, "day")
          .format("YYYY-MM-DD")
      } else {
        projectionCompletionDate = dayjs(lastState.end_state_date).format("YYYY-MM-DD")
      }
    } else {
      const ogState =
        workflowTrackerData.state_data.tracker_data[workflowTrackerData.state_data.tracker_data.length - 1]
      projectionCompletionDate = dayjs(ogState.end_state_date).format("YYYY-MM-DD")
    }

    const onHoldIndex = workflowTrackerData.state_data.tracker_data.findIndex(
      (state) => state.state_title === "On Hold"
    )

    stateData.forEach((state, i) => {
      let formattedState = {}
      if (isOnHold && onHoldIndex !== -1 && i >= onHoldIndex) {
        const ogState = workflowTrackerData.state_data.tracker_data[i - 1]
        formattedState = {
          ...ogState,
          start_state_date: formatToUtc(ogState.start_state_date),
          end_state_date: formatToUtc(ogState.end_state_date),
          assign_to_ids: state.assign_to_ids || state.assigned_to_ids || [],
          is_first_state: i === 0,
          is_last_state: i === stateData.length - 1,
        }
      } else {
        let currState = { ...state }
        let startDate = state.start_state_date
        let endDate = stateData[i + 1]?.start_state_date || startDate
        let goalDays = dayjs(endDate).diff(startDate, "day")

        // Adjust start date for non-first/last states
        if (i !== 0 && i !== stateData.length - 1 && stateData[i - 1].end_state_date) {
          if (!dayjs(startDate).isSame(stateData[i - 1].end_state_date, "day")) {
            startDate = stateData[i - 1].end_state_date
          }
        }

        // Handle invalid dates and goal days
        if (dayjs(startDate).diff(state.end_state_date, "day") < 0) {
          currState.end_state_date = startDate
        }
        if (goalDays < 0) {
          goalDays = 0
          endDate = startDate
          if (actionData[i - 1]) {
            actionData[i - 1].end_state_date = startDate
            actionData[i - 1].goal_days = getDifferenceBetweenDays(startDate, actionData[i - 1].start_state_date)
          }
        }

        formattedState = {
          ...currState,
          is_first_state: i === 0,
          is_last_state: i === stateData.length - 1,
          assign_to_ids: currState.assign_to_ids || currState.assigned_to_ids || [],
          start_state_date: formatToUtc(startDate),
          end_state_date: formatToUtc(endDate),
          goal_days: goalDays || 0,
        }
      }

      actionData.push(payloadCleanup(formattedState))
    })

    const req = {
      model,
      projected_completion_date: projectionCompletionDate,
      action_data: actionData,
    }

    if (activityDueDate) req.due_date = activityDueDate
    if (assessmentPk) req.assessment_id = assessmentPk
    if (workflowTrackerData?.parent_data?.actual_completion_date) {
      req.actual_completion_date = dayjs(stateData[stateData.length - 1].start_state_date).format("YYYY-MM-DD")
    }
    if (workflowTrackerData?.parent_data?.activity_due_date) {
      req.activity_due_date = workflowTrackerData.parent_data.activity_due_date
    }

    return req
  }

  const setFormattedWorkflowTrackerData = (data) => {
    try {
      const trackerData = data?.state_data?.tracker_data
      if (!trackerData || trackerData.length === 0 || trackerData[0].start_state_date?.includes("T") === false) {
        setWorkflowTrackerData(data)
        return
      }

      const trackerWithDates = cloneDeep(trackerData)

      let tz = "00:00:00Z"

      const normalizeTz = (date) => {
        if (!date) return tz
        let splitDate = date.split("T")
        const dateTz = splitDate[1]
        if (dateTz !== tz && dateTz !== "00:00:00Z") tz = dateTz

        return `${splitDate[0]}T${tz}`
      }

      // normalize timezones
      for (let i = 0; i < trackerWithDates.length; i++) {
        let curr = trackerWithDates[i]
        if (curr.start_state_date) curr.start_state_date = normalizeTz(curr.start_state_date)
        if (curr.end_state_date) curr.end_state_date = normalizeTz(curr.end_state_date)
      }

      // adjust dates based on goal days
      for (let i = 0; i < trackerWithDates.length; i++) {
        const curr = trackerWithDates[i]
        const next = trackerWithDates[i + 1]
        const prev = trackerWithDates[i - 1]

        // fill start date of first step
        if (i === 0 && !curr.start_state_date) {
          curr.start_state_date = curr.end_state_date
        } else if (i === trackerWithDates.length - 1) {
          // fill end date of last step
          curr.start_state_date = prev.end_state_date
          curr.end_state_date = curr.start_state_date
          curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
        } else if (i > 0 && i < trackerWithDates.length - 1) {
          if (curr.status === "Completed") {
            if (!dayjs(curr.start_state_date).isSame(prev.end_state_date)) {
              curr.start_state_date = prev.end_state_date
              curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
            } else {
              curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
              if (curr.start_state_date !== prev.end_state_date) {
                prev.end_state_date = curr.start_state_date
                prev.goal_days = getDifferenceBetweenDays(prev.start_state_date, prev.end_state_date)
              }
            }
          } else if (curr.status === "Bypassed") {
            curr.goal_days = 0
            curr.start_state_date = prev.end_state_date
            curr.end_state_date = prev.end_state_date
          } else if (curr.is_current) {
            if (next.prev_action_id !== curr.action_id) {
              const endDate = addDays(curr.start_state_date, curr.goal_days)
              curr.end_state_date = endDate
              next.start_state_date = endDate
            } else {
              curr.start_state_date = prev.end_state_date
              curr.end_state_date = next.start_state_date
            }
          } else {
            curr.start_state_date = prev.end_state_date
          }
        }

        if (!curr.goal_days) {
          if (curr.start_state_date && curr.end_state_date) {
            curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
          } else if (curr.start_state_date && next) {
            curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, next.start_state_date)
          } else if (curr.end_state_date && prev) {
            curr.goal_days = getDifferenceBetweenDays(curr.end_state_date, prev.end_state_date)
          }
        }
      }

      // adjust timezone updates
      for (let i = 0; i < trackerWithDates.length; i++) {
        const curr = trackerWithDates[i]
        curr.start_state_date = convertUtcToLocal(curr.start_state_date)
        curr.end_state_date = convertUtcToLocal(curr.end_state_date)
        curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
      }

      for (let i = trackerWithDates.length - 1; i >= 0; i--) {
        let curr = trackerWithDates[i]
        let prev = trackerWithDates[i - 1]

        if (dayjs(curr.start_state_date).isAfter(curr.end_state_date)) {
          curr.start_state_date = curr.end_state_date
          if (prev) {
            prev.end_state_date = curr.start_state_date
          }
        } else if (prev && dayjs(curr.start_state_date).isBefore(prev.end_state_date)) {
          prev.end_state_date = curr.start_state_date
        }
      }

      // reset goal days
      for (let i = 0; i < trackerWithDates.length; i++) {
        const curr = trackerWithDates[i]
        curr.goal_days = getDifferenceBetweenDays(curr.start_state_date, curr.end_state_date)
      }
      setWorkflowTrackerData({
        ...data,
        state_data: {
          ...data.state_data,
          tracker_data: trackerWithDates,
        },
      })
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <WorkflowContext.Provider
      value={{
        /** LOCAL STATE (could pass as props) */

        // workflow ID
        workflowId, // EditWorkflowModal, PizzaTrackerContainer
        setWorkflowId, // Activity, Request, Activities

        // workflow model
        workflowModel, // CancelWorkflowModal, EditWorkflowModal, ForceStepModal
        setWorkflowModel, // Activity, Request, Activities

        // selected action
        selectedAction, // ForceStepModal
        setSelectedAction, // Activity

        // force modal state
        openForceModal, // ForceStepModal
        setOpenForceModal, // ForceStepModal, TrackerHeader

        // edit state panel state
        openEditStatesModal, // EditWorkflowModal
        setOpenEditStatesPanel, // EditWorkflowModal, TrackerHeader

        // cancel workflow modal state
        setOpenCancelWorkflowModal, // CancelWorkflowModal, TrackerHeader
        openCancelWorkflowModal, // CancelWorkflowModal

        // parent queries
        setParentQueries, // Activity, Request
        invalidateParentQueries, // CancelWorkflowModal, EditWorkflowModal, ForceStepModal, TrackerHeader

        /** API MUTATE FUNCTIONS (could localize) */

        forceWorkflow, // ForceStepModal
        cancelWorkflow, // CancelWorkflowModal
        holdWorkflow, // TrackerHeader
        unholdWorkflow, // ForceStepModal

        /** TODO: SERVER STATE - do not save server state into local state; remove from context provider and localize */

        workflowTrackerData, // WorkflowTracker, EditWorkflowModal, ForceStepModal, PizzaTrackerContainer, Activities
        setWorkflowTrackerData: setFormattedWorkflowTrackerData, // WorkflowTracker, PizzaTrackerContainer, Activities

        /** TODO: DERIVED STATE - make following derived (from server) state into pure functions or just localize */

        // generate put request payload
        formatPutReq,

        // Date difference and add fucntions
        getDifferenceBetweenDays,
        addDays,

        isWorkflowCancelled, // State (WorkflowTracker/Tracker), TrackerHeader, TrackerStatus
        isOnHold, // EditWorkflowModal, State (WorkflowTracker/Tracker), TrackerHeader
        isWorkflowComplete, // WorkflowTracker, EditWorkflowModal, State (WorkflowTracker/Tracker), TrackerHeader, TrackerStatus, PizzaTrackerContainer
        // trackerData, // ForceStepModal, TrackerStatus
        currentState, // ForceStepModal, TrackerHeader
        projectedCompletionDate, // useRequestApi, RequestOverview
        completedStates, // TrackerHeader
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}

const useWorkflowTracker = () => {
  return useContext(WorkflowContext)
}

export default useWorkflowTracker
